import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from 'react-router-dom';

import Loader from '../../components/Loader';
import { toast } from 'react-toastify';
import ApiService from '../../services/ApiService';
import {formatDate, getNextStatus} from '../../utils/helper.js';
import {statusColor} from "../../assets/utils/statusColors";
import Swal from 'sweetalert2';
import { selectStoredPermissions } from '../../stores/permissionsSlice';


const OrderDetails = () => {

  const { id } = useParams();
  const navigate = useNavigate();

  const [isLoader, setIsLoader] = useState(false);
  const [currency] = useState(process.env.REACT_APP_CURRENCY);
  const [tab, setTab] = useState("info");
  const [order, setOrder] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [next_status, setNextStatus] = useState('');
  const [remainingTime, setRemainingTime] = useState(null);
  const [remainingTimeColor, setRemainingTimeColor] = useState(null);

  useEffect(() => {
    if (!order?.createdAt || !order?.branch?.preparing_time) return;

    // Parse preparing time (assuming format "HH:MM")
    let prepMinutes;
    if (order.type== "delivery") {
       prepMinutes= Number(order.branch.preparing_time) + Number(order.branch.delivery_time);
    }else{
       prepMinutes= Number(order.branch.preparing_time);
    }
    const prepTimeInMs = (prepMinutes * 60 * 1000);

    const orderTime = new Date(order.createdAt).getTime();
    const deadline = orderTime + prepTimeInMs;
    
    const updateTimer = () => {
      const now = new Date().getTime();
      const timeLeft = deadline - now; // Can be negative

      const hours = Math.floor(Math.abs(timeLeft) / (1000 * 60 * 60));
      const minutes = Math.floor((Math.abs(timeLeft) % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((Math.abs(timeLeft) % (1000 * 60)) / 1000);

      // Add a "-" prefix if timeLeft is negative
      const sign = timeLeft < 0 ? "-" : "";
      const signColor = timeLeft < 0 ? setRemainingTimeColor('danger') : setRemainingTimeColor('success');
      // setRemainingTime(`${sign}${hours}h ${minutes}m ${seconds}s`);
      setRemainingTime(`${sign}${minutes}m ${seconds}s`);
    };

    updateTimer();
    const timerInterval = setInterval(updateTimer, 1000);

    return () => clearInterval(timerInterval);
  }, [order]);


  useEffect(() => {
    if (id) {
      getSingleData();
    }
    getSettings();
  }, [id]);

  const getSingleData = async () => {
    try {
      setIsLoader(true);
      const response = await ApiService.request({
        method: 'GET',
        url: `orders/${id}`,
      });
      const data = response.data;
      if (data.status) {
        const item = data.data;
        setOrder(item);
        setNextStatus(getNextStatus(item?.status));
      } else {
        toast.error(data.message);
      }
      setIsLoader(false);
    } catch (error) {
      setIsLoader(false);
    }
  };

  
  const getSettings = async () => {
    try {
      setIsLoader(true);
      const response = await ApiService.request({
        method: 'GET',
        url: `getSettings`,
      });
      const data = response.data;
      if (data.status) {
        const item = data.data;
        setReasons(item.cancel_reasons);
      } else {
        toast.error(data.message);
      }
      setIsLoader(false);
    } catch (error) {
      setIsLoader(false);
    }
  };

  const updateStatus = async (nstatus = null) => {
    const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'You want to update order status',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#47be7d',
        confirmButtonText: 'Yes',
        cancelButtonColor: '#9E9B9B',
        cancelButtonText: 'No',
    });
    let up_status=next_status;
    if (nstatus) {
      up_status=nstatus;
    }

    if (result.isConfirmed) {
        const response = await ApiService.request({
            method: "POST",
            url: `orders/status`, // Replace with your API endpoint
            data: {order_id:id, status:up_status}
        });
        const data = response.data;
        if (data.status) {
            getSingleData();
            toast.success(data.message);
        } else {
            toast.error(data.message);
        }
    }
};

const cancelStatus = async () => {
   const optionsHTML = reasons
    .map(reason => `<option value="${reason}">${reason}</option>`)
    .join('');

  const { value: formValues } = await Swal.fire({
    title: 'Cancel Order',
    html:
      '<div class="text-left">' +
      '<p>Are you sure you want to cancel this order?</p>' +
      '<select id="swal-reason" class="swal2-select mt-3 w-full">' +
      '  <option value="" selected disabled>Select cancellation reason</option>' +
      optionsHTML +
      '</select>' +
      '<textarea id="swal-notes" class="swal2-textarea mt-3 w-full" placeholder="Additional notes (optional)" style="display: none;"></textarea>' +
      '</div>',
    icon: 'warning',
    focusConfirm: false,
    showCancelButton: true,
    confirmButtonColor: '#D8214E',
    cancelButtonColor: '#9E9B9B',
    confirmButtonText: 'Cancel',
    cancelButtonText: 'No',
    preConfirm: () => {
      const reason = document.getElementById('swal-reason').value;
      
      if (!reason) {
        Swal.showValidationMessage('Please select a cancellation reason');
        return false;
      }
      
      return {
        reason,
      };
    },
  
  });

  if (formValues) {
    try {
      const response = await ApiService.request({
        method: "POST",
        url: `orders/status`,
        data: {
          order_id: id,
          status: 'Cancelled',
          cancel_reason: formValues.reason ,
        }
      });

      const data = response.data;
      if (data.status) {
        getSingleData();
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to cancel order');
    }
  }
};




  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div
          id="kt_app_toolbar_container"
          className="app-container container-xxl d-flex flex-stack"
        >
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              Order #{order?.order_code}
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <Link to="/dashboard" className="text-muted text-hover-primary">
                  Dashboard
                </Link>
              </li>
              <li className="breadcrumb-item">
                <span className="bullet bg-gray-400 w-5px h-2px"></span>
              </li>
              <li className="breadcrumb-item text-muted">
                <Link to="/orders" className="text-muted text-hover-primary">
                  Orders
                </Link>
              </li>
            </ul>
          </div>
          <div className="d-flex align-items-center gap-2 gap-lg-3">
              {order.status !== "Delivered" &&
                <span className={`me-3 fs-3 text-${remainingTimeColor}`}>
                    {remainingTime === null 
                      ? "Calculating..." 
                      : remainingTime === "Time Up"
                        ? <span className="text-danger">Time Up</span>
                        : `${remainingTime}`}
                  </span>
              }
                

                <button type="button" className="btn btn-sm btn-secondary me-3" onClick={() => navigate(-1)}>
                    <i className="bi bi-arrow-left-short fs-1"></i>  Back
                </button>
                {( !isLoader && selectStoredPermissions().includes('status-orders') ) &&
                  <div className="buttons">
                    {
                      (order.status !== 'Delivered' && order.status !== 'Cancelled') && 
                        (order.type == 'pickup' && order.status == 'Picked_Up'  ? (
                          <span></span>
                          // <button className="btn btn-lg btn-success mx-5" onClick={() => updateStatus()}>
                          //   <i className="bi bi-check-lg fs-2"></i> {next_status}
                          // </button>
                        ) : <button className="btn btn-lg btn-success mx-5" onClick={() => updateStatus()}>
                            <i className="bi bi-check-lg fs-2"></i> {next_status}
                          </button>)
                    }
                    {(  order.status !=='Cancelled') && 
                      <button className="btn btn-lg btn-danger mx-5"  onClick={ () => cancelStatus()}><i className="bi bi-x-circle-fill fs-2"></i> {order.status == 'Placed'?'Reject':'Cancel'} </button>
                    }
                  </div>
                }
          </div>
          
        </div>
      </div>

      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">
          <div className="card">
            <div className="card-body pt-5">

                <ul class="nav nav-tabs" id="myTab" role="tablist">
                  <li class="nav-item" role="presentation">
                    <button  className={`nav-link fs-6 px-5 ${tab === "info" ? "active" : ""}`}
                            onClick={() => setTab("info")}
                     type="button"> <i className="bi bi-box-fill fs-5"></i> Info</button>
                  </li>
                  <li class="nav-item" role="presentation">
                    <button className={`nav-link fs-6 px-5 ${tab === "items" ? "active" : ""}`}
                            onClick={() => setTab("items")}
                      type="button" ><i className="bi bi-cup-straw fs-5"></i> Order Items</button>
                  </li>

                  <li class="nav-item" role="presentation">
                    <button className={`nav-link fs-6 px-5 ${tab === "user" ? "active" : ""}`}
                            onClick={() => setTab("user")}
                      type="button" ><i className="bi bi-person-fill fs-5"></i> User</button>
                  </li>
                  <li class="nav-item" role="presentation">
                    <button className={`nav-link fs-6 px-5 ${tab === "branch" ? "active" : ""}`}
                            onClick={() => setTab("branch")}
                      type="button" ><i className="bi bi-building fs-5"></i> Branch</button>
                  </li>
                  {order.type === 'delivery' &&
                    <li class="nav-item" role="presentation">
                      <button className={`nav-link fs-6 px-5 ${tab === "address" ? "active" : ""}`}
                              onClick={() => setTab("address")}
                        type="button" ><i className="bi bi-geo-alt-fill fs-5"></i> Delivery Address</button>
                    </li>
                  }

                 
                </ul>

                {!isLoader && 
                  <div class="tab-content" id="myTabContent">
                    {tab === "info" && (
                      <div class="tab-pane fade show active" >
                        <div className="table-responsive mt-5 w-lg-75">
                          <table className="table align-middle table-striped border fs-6">
                            <tbody>
                              <tr>
                                <th className="px-3 fw-semibold">Order</th>
                                <th>#{order?.order_code}</th>
                              </tr>
                              <tr>
                                <th className="px-3 fw-semibold">Payment Method</th>
                                <td> {order?.payment_method}</td>
                              </tr>
                              <tr>
                                <th className="px-3 fw-semibold">Order From</th>
                                <td> {order?.order_from}</td>
                              </tr>
                              <tr>
                                <th className="px-3 fw-semibold">Delivery Instruction</th>
                                <td> {order?.delivery_instruction}</td>
                              </tr>
                              <tr>
                                <th className="px-3 fw-semibold">Order Time</th>
                                <td>{formatDate(order?.createdAt,'DD MMM, hh:mm A')} </td>
                              </tr>
                              
                              <tr>
                                <th className="px-3 fw-semibold">Total Price</th>
                                <td>{currency} {order?.total_price}</td>
                              </tr>
                              <tr>
                                <th className="px-3 fw-semibold">Delivery Charges</th>
                                <td>{currency} {order?.delivery_charges}</td>
                              </tr>
                              <tr>
                                <th className="px-3 fw-semibold">Vat</th>
                                <td>{currency} {order?.vat_amount}</td>
                              </tr>
                              {/* <tr>
                                <th className="px-3 fw-semibold">Platform Fee</th>
                                <td>{currency} {order?.platform_fee}</td>
                              </tr> */}
                              <tr>
                                <th className="px-3 fw-semibold">Promo Code </th>
                                <td> 
                                    {order?.promo_code ?(
                                      <div>
                                        <span className="badge bg-base text-white">{order?.promo_code} </span>
                                          <span> {currency} {order?.promo_discount}</span>
                                      </div>
                                    ):(
                                      <div>  N/A </div>
                                    )}
                                  </td>
                              </tr>
                              <tr>
                                <th className="px-3 fw-semibold">Loyalty </th>
                                <td> 
                                      <div>
                                        <span className={`badge text-capitalize ${(order?.points_type=='reward')?'bg-success':'bg-danger'}`} >{order?.points_type} </span> <br />
                                        {order?.points_type=='reward' ?(
                                          <span> {order?.loyalty_points} Points</span>
                                        ):(
                                          <span> {currency} {order?.loyalty_discount}</span>
                                        )}
                                      </div>
                                
                                  </td>
                              </tr>
                              <tr>
                                <th className="px-3 fw-semibold">Grand Total</th>
                                <td>{currency} {order?.grand_total}</td>
                              </tr>
                            
                            
                            </tbody>
                          
                          </table>
                        </div>
                      </div>
                    )}
                    {tab === "items" && (
                      <div class="tab-pane fade show active">
                        <div className="table-responsive mt-5 w-100">
                          <table className="table align-middle table-striped border fs-6">
                            <tbody>
                              <tr>
                                <th className="px-3 fw-semibold" colSpan={2}>Item</th>
                                {/* <th className="px-3 fw-semibold">Addons</th> */}
                                <th className="px-3 fw-semibold">Instruction</th>
                                <th className="px-3 fw-semibold">Price</th>
                                <th className="px-3 fw-semibold">Quantity</th>
                                <th className="px-3 fw-semibold">Total Price</th>
                              </tr>
                              {order.order_items.length > 0 ? order.order_items.map((item, index) => (
                                <tr key={index} className=" bg-white my-3  border-top">
                                  <td> 
                                    <img src={item?.item?.image} alt="item" className="w-60px h-60px mx-2 object-cover rounded" />
                                  </td>

                                  <td> 
                                    <p className="fw-bold text-base">  {item?.item?.title} </p>

                                    {item.addons.map((addon, index) => (
                                      <p className="text-gray-700">{addon.item_id.title} </p> 
                                    ))}
                                  </td>

                                 
                                  

                                  <td>{item.instruction ?? '--'}</td>
                                  <th> 
                                    
                                   <p className="fw-bold text-base">{currency} {item.total_price}</p> 
                                    {item.addons.map((addon, index) => (
                                      <p className="text-gray-700">{addon.item_id.price} </p> 
                                    ))}
                                  
                                  </th>
                                  <th>  
                                    <p className="fw-bold text-base">{item?.quantity} </p> 
                                      {item.addons.map((addon, index) => (
                                        <p className="text-gray-700">{addon.quantity} </p> 
                                      ))}
                                  </th>
                                  <th> 
                                    <p className="fw-bold text-base">{currency} {item.total_price }</p>
                                    {item.addons.map((addon, index) => (
                                        <p className="text-gray-700">{currency} {addon.total_price} </p> 
                                    ))}
                                  </th>
                                </tr>
                              )) : (
                                  <tr>
                                      <td colSpan="6" className="text-dark text-center mt-2">No data found!</td>
                                  </tr>
                              )}

                              {order.order_items.length > 0 &&
                                  <tr>
                                      <td colSpan="5" className="text-end fw-bold text-center mt-2">Sub Total</td>

                                      <td colSpan="2" className="text-dark fw-bold  mt-2">{currency} {order?.total_price.toFixed(2)}</td>
                                  </tr>
                              }
                             
                            </tbody>
                          
                          </table>
                        </div>
                      </div>
                    )}

                    {tab === "user" && (
                      <div class="tab-pane fade show active">
                        <div className="table-responsive mt-5 w-lg-75">
                          <table className="table align-middle table-striped border fs-6">
                            <tbody>
                              <tr>
                                <th className="px-3 fw-semibold">Name</th>
                                <th>{order?.user?.name}</th>
                              </tr>
                              <tr>
                                <th className="px-3 fw-semibold">Email</th>
                                <td> {order?.user?.email}</td>
                              </tr>
                             
                              <tr>
                                <th className="px-3 fw-semibold">Phone</th>
                                <td> {order?.user?.phone}</td>
                              </tr>
                             
                            </tbody>
                          
                          </table>
                        </div>
                      </div>
                    )}
                    {tab === "branch" && (
                      <div class="tab-pane fade show active">
                        <div className="table-responsive mt-5 w-lg-75">
                          <table className="table align-middle table-striped border fs-6">
                            <tbody>
                              <tr>
                                <th className="px-3 fw-semibold">Name</th>
                                <th>{order?.branch?.name}</th>
                              </tr>
                              <tr>
                                <th className="px-3 fw-semibold">Email</th>
                                <td> {order?.branch?.email}</td>
                              </tr>
                              <tr>
                                <th className="px-3 fw-semibold">Phone</th>
                                <td> {order?.branch?.phone}</td>
                              </tr>
                             
                            </tbody>
                          
                          </table>
                        </div>
                      </div>
                    )}
                    {tab === "address" && (
                      <div class="tab-pane fade show active">
                        <div className="table-responsive mt-5 w-lg-75">
                          <table className="table align-middle table-striped border fs-6">
                            <tbody>
                              <tr>
                                <th className="px-3 fw-semibold">Type</th>
                                <th>{order?.user_address?.type}</th>
                              </tr>
                              <tr>
                                <th className="px-3 fw-semibold">Apt/Villa No</th>
                                <td> {order?.user_address?.villa_apt_no}</td>
                              </tr>
                              
                              <tr>
                                <th className="px-3 fw-semibold">Area</th>
                                <td> {order?.user_address?.area}</td>
                              </tr>
                              <tr>
                                <th className="px-3 fw-semibold">Address</th>
                                <td> {order?.user_address?.address}</td>
                              </tr>
                              <tr>
                                <th className="px-3 fw-semibold">Map View</th>
                                <td>
                                    <iframe
                                        width="600"
                                        height="300"
                                        loading="lazy"
                                        allowFullScreen
                                        referrerPolicy="no-referrer-when-downgrade"
                                        src={`https://www.google.com/maps?q=${order?.user_address?.lat},${order?.user_address?.lng}&hl=es&z=14&output=embed`}
                                      ></iframe>  
                                </td>
                              </tr>
                             
                            </tbody>
                          
                          </table>
                        </div>
                      </div>
                    )}

                    {tab === "review" && (
                      <div class="tab-pane fade show active">
                        <div className="table-responsive mt-5 w-lg-75">
                          <table className="table align-middle table-striped border fs-6">
                            <tbody>
                              <tr>
                                <th className="px-3 fw-semibold">Rating</th>
                                <th>{order?.review?.rating} <i className="bi bi-star-fill text-warning fs-5"></i> </th>
                              </tr>
                              <tr>
                                <th className="px-3 fw-semibold">Comments</th>
                                <td> {order?.review?.review}</td>
                              </tr>
                             
                            
                             
                            </tbody>
                          
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                }
                {isLoader && 
                  <Loader color="black" className="mt-5" text="Loading" size="30" />
                }
             
             
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;
